import { create } from "zustand";

export const useKpiStore = create((set, get) => ({
    data: [],
    loading: false,
    error: null,
    pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 5,
    },
    searchQuery: "",
    columnFilters: {},
    baseParams: {},
    uniqueValues: {},
    sort: null,

    setData: (data) => set({ data }),
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
    setPagination: (pagination) => set((state) => ({ pagination: { ...state.pagination, ...pagination } })),
    setUniqueValues: (uniqueValues) => set({ uniqueValues }),

    fetchData: async (params = {}) => {
        const { kpiIntegration } = await import("@/lib/api/client");
        return await kpiIntegration.fetchKpis(params);
    },

    fetchRelatedKpis: async (objectiveId, params = {}) => {
        if (!objectiveId) return;
        const { objectiveIntegration } = await import("@/lib/api/client");
        return await objectiveIntegration.fetchKpis(objectiveId, params);
    },

    linkKpis: async (objectiveId, selectedIds) => {
        try {
            const { objectiveIntegration } = await import("@/lib/api/client");
            const response = await objectiveIntegration.addKpis(objectiveId, selectedIds);
            if (response.success) {
                await get().fetchRelatedKpis(objectiveId);
            }
            return response;
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    unlinkKpi: async (objectiveId, kpiId, opts = {}) => {
        try {
            const { objectiveIntegration } = await import("@/lib/api/client");
            const response = await objectiveIntegration.removeKpi(objectiveId, kpiId, opts);
            if (response.success) {
                await get().fetchRelatedKpis(objectiveId);
            }
            return response;
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    createItem: async (objectiveId, formData) => {
        try {
            const { kpiIntegration } = await import("@/lib/api/client");
            const response = await kpiIntegration.createKpi(formData);
            if (response.success && objectiveId) {
                // Link the newly created KPI to the objective if objectiveId is provided
                await get().linkKpis(objectiveId, [response.data._id || response.data.id]);
            }
            return response.success;
        } catch (error) {
            return false;
        }
    },

    updateItem: async (objectiveId, kpiId, formData) => {
        try {
            const { kpiIntegration } = await import("@/lib/api/client");
            const response = await kpiIntegration.updateKpi(kpiId, formData);
            if (response.success && objectiveId) {
                await get().fetchRelatedKpis(objectiveId);
            }
            return response.success;
        } catch (error) {
            return false;
        }
    },

    deleteItem: async (objectiveId, kpiId) => {
        try {
            const { kpiIntegration } = await import("@/lib/api/client");
            const response = await kpiIntegration.deleteKpi(kpiId);
            if (response.success && objectiveId) {
                await get().fetchRelatedKpis(objectiveId);
            }
            return response.success;
        } catch (error) {
            return false;
        }
    },

    setSearchQuery: (query) => set({ searchQuery: query }),
    setColumnFilters: (filters) => set({ columnFilters: filters }),
    setBaseParams: (params) => set({ baseParams: params }),
    setSort: (sort) => set({ sort }),
    clearError: () => set({ error: null }),
}));
